import { isEqual, isUndefined, kebabCase } from 'lodash';

/**
 * Carries task item attrs that have no visible HTML representation - the chip data - through
 * the HTML clipboard, as private JSON `data-*` attributes on each task item `<li>`.
 *
 * Every attr is namespaced under `data-mn-task-`, so it is obvious the value is Milanote's own
 * clipboard metadata rather than markup any other consumer should read.
 *
 * Copied HTML can be edited, damaged, or written by another app entirely, so decoding treats
 * the attribute as untrusted: a missing, empty, or syntactically invalid value falls back to the
 * attr's schema default rather than throwing.
 *
 * Syntax is all `JSON.parse` can vouch for - `"5"` and `"\"x\""` decode cleanly to values no chip
 * attr ever holds - so every caller supplies an `isValid` predicate for the shape it expects, and
 * a decoded value failing it falls back to the default as well.
 */

// Builds the private attribute name for an attr, e.g. `dueDate` -> `data-mn-task-due-date`
export const getTaskItemHtmlAttrName = (attrName: string): string => `data-mn-task-${kebabCase(attrName)}`;

/**
 * Renders `value` as the private `data-*` attribute for `attrName`, shaped for a Tiptap
 * attribute's `renderHTML`.
 *
 * A value matching the schema default is omitted, so copying ordinary tasks does not bloat the
 * clipboard HTML with metadata that says nothing. Decoding falls back to the same default, so an
 * omitted attribute still round-trips to the value it was rendered from.
 */
export const renderTaskItemHtmlAttr = ({
    attrName,
    value,
    defaultValue,
}: {
    attrName: string;
    value: unknown;
    defaultValue: unknown;
}): Record<string, string> => {
    // `undefined` has no JSON representation, and reads back as the default either way.
    if (isUndefined(value) || isEqual(value, defaultValue)) return {};

    return { [getTaskItemHtmlAttrName(attrName)]: JSON.stringify(value) };
};

/**
 * Reads the private `data-*` attribute for `attrName` off `element`, shaped for a Tiptap
 * attribute's `parseHTML`.
 *
 * Returns `defaultValue` for a missing, empty, or unparseable attribute, and for a decoded value
 * `isValid` rejects - e.g. `Array.isArray` for an attr holding a list; the codec itself knows
 * nothing about what any attr holds. Never throws, including for a predicate that does.
 */
export const parseTaskItemHtmlAttr = ({
    element,
    attrName,
    defaultValue,
    isValid,
}: {
    element: HTMLElement;
    attrName: string;
    defaultValue: unknown;
    isValid: (value: unknown) => boolean;
}): unknown => {
    const encoded = element.getAttribute(getTaskItemHtmlAttrName(attrName));
    if (!encoded) return defaultValue;

    try {
        const decoded: unknown = JSON.parse(encoded);

        return isValid(decoded) ? decoded : defaultValue;
    } catch {
        return defaultValue;
    }
};
